import { Button, ButtonGroup, Container, Grid, InputLabel } from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import { parseISO } from "date-fns";
import { Formik, FormikProps } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Translation, useTranslation } from "react-i18next";
import errorConstants from "../../constants/error.constants";
import {
  selectDefaultRevocationEmailValue,
  selectEnableDelegationsScheduling,
} from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import useAppContext from "../../context/hooks/useAppContext";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import useImportDelegationsContext from "../../context/hooks/useImportDelegationsContext";
import { DelegationType } from "../../context/store/reducers/fleetManagerContext.reducer";
import useValidationSchema, { IDelegeeFormValues, IRightSelection } from "../../hooks/useValidationSchema";
import { ParkingRightsRequestModel } from "../../models/ParkingRightsRequestModel";
import { useAppSelector } from "../../store";
import { variables } from "../../theme/variables";
import { getLocalizedDate } from "../../utils/date.utils";
import WarningColumn from "../../ui/CenteredWarning/CenteredWarning";
import CustomButton from "../../ui/CustomButton/CustomButton";
import CustomError from "../../ui/CustomError/CustomError";
import CustomInput from "../../ui/CustomInput/CustomInput";
import Heading from "../../ui/Heading/Heading";
import RequiredInputLabel from "../../ui/RequiredInputLabel/RequiredInputLabel";
import Spacer from "../../ui/Spacer/Spacer";
import { checkEmailExists, checkHasNoCapacity, checkValidationError } from "../../utils";
import { DeleteDelegeeIcon, DeleteText, DeleteWrapper } from "../DataTable/DataTableFilters/CommonStyledComponents";
import { NoPaddingBottomGrid } from "../DataTable/DataTableStyledComponents";
import SchedulingDateRangePicker, { SchedulingDateRangePickerError } from "../SchedulingDateRangePicker/SchedulingDateRangePicker";
import "./_delegationForm.scss";
import { DelegationFormDetails } from "./DelegationFormDetails";
import ParkingRightsGrid, { ICheckParkingRight } from "./ParkingRightsGrid/ParkingRightsGrid";
import { Product } from "../../models/InvitePedestrianRightRequestModel";

const { typography } = variables;
interface IFormProps {
  onClose: () => void;
  refreshHandler: () => void;
  getProducts: (seasonTicketOwnerCrmId: string, language: string) => void;
  inviteDelegee: (parkingRights: ParkingRightsRequestModel, callback?: () => void) => void;
  updateDelegation: (parkingRights: ParkingRightsRequestModel, callback?: () => void) => void;
  getDelegeeDetails: (delegeeId: string, language: string, seasonTicketOwnerCrmId: string) => void;
  resetBulkImportProgress: () => void;
  clearDelegeeDetails: () => void;
  getBulkImportStatus: (seasonTicketOwnerCrmId: string) => Promise<unknown> | void;
  disposeError: () => void;
  isEditMode: boolean;
  onDelete?: () => void;
  translationContextName: string;
  disableMultipleOption?: boolean;
  delegationFormDetails: DelegationFormDetails;
  shouldBlockForSingle?: boolean;
}

const DelegationForm: React.FC<IFormProps> = (props) => {
  const {
    onClose,
    refreshHandler,
  } = props;

  const { appState } = useAppContext();
  const { fleetManagerState, toggleDeleteDelegeeWarning, setDelegationType, setDragAndDropView } =
    useFleetManagerContext();
  const onDelete = props.onDelete || toggleDeleteDelegeeWarning;
  const defaultRevocationEmailValue = useAppSelector(selectDefaultRevocationEmailValue);
  const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
  const [sendRevocationConfirmationEmail, setSendRevocationConfirmationEmail] = useState(defaultRevocationEmailValue);
  const [schedulingErrors, setSchedulingErrors] = useState<Record<string, SchedulingDateRangePickerError | undefined>>({});
  // The bulk import status is checked every time the multiple option is selected.
  const [isBulkImportStatusChecked, setIsBulkImportStatusChecked] = useState(false);
  const bulkImportStatusRequestId = useRef(0);
  const isMounted = useRef(true);

  const isEditMode = props.isEditMode;

  const { t } = useTranslation(["delegeeForm", props.translationContextName, "globals", "errors", "vehicleForm"]);
  const { validationSchema, initialValues } = useValidationSchema(
    t,
    isEditMode,
    fleetManagerState.delegationType,
    props.translationContextName
  );

  const innerRef = useRef<FormikProps<IDelegeeFormValues>>(null);

  const getProductDateKey = (pmcId: number, contractType: number) => `${pmcId}_${contractType}`;

  const toDateOnly = (date: Date): Date => {
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    return dateOnly;
  };

  const parseDateValue = (date: Date | string): Date | null => {
    if (date instanceof Date) {
      return Number.isNaN(date.valueOf()) ? null : toDateOnly(date);
    }

    const parsedDate = parseISO(date.split("T")[0]);
    return Number.isNaN(parsedDate.valueOf()) ? null : parsedDate;
  };

  // Keeps the original time (e.g. end-of-day) instead of truncating to midnight like parseDateValue does.
  // Strips the UTC offset so the literal date/time digits aren't shifted across the browser's local timezone.
  const parseExpirationDateValue = (date: Date | string): Date | null => {
    const parsedDate = date instanceof Date ? date : parseISO(date.replace(/(Z|[+-]\d{2}:?\d{2})$/, ""));
    return Number.isNaN(parsedDate.valueOf()) ? null : parsedDate;
  };

  const getPrefillActivationDate = (pmcId: number, contractType: number): Date => {
    const parkingProduct = props.delegationFormDetails.parkingProducts.data?.find(
      (product) => product.pmcId === pmcId && product.contractType === contractType
    );
    const firstAvailableStartDate = parkingProduct?.firstAvailableContractStartDate;
    if (!firstAvailableStartDate) {
      return new Date();
    }

    return parseDateValue(firstAvailableStartDate) ?? new Date();
  };

  const getPrefillExpirationDate = (pmcId: number, contractType: number): Date | null => {
    const expirationDate = props.delegationFormDetails.parkingProducts.data?.find(
      (product) => product.pmcId === pmcId && product.contractType === contractType
    )?.expirationDate;
    return expirationDate ? parseExpirationDateValue(expirationDate) : null;
  };

  const ensureProductDatesInitialized = (right: IRightSelection) => {
    const productDateKey = getProductDateKey(right.pmcId, right.contractType);
    if (enableDelegationsScheduling && !innerRef.current?.values.productDates?.[productDateKey]?.activationDate) {
      innerRef.current?.setFieldValue(
        `productDates.${productDateKey}.activationDate`,
        getPrefillActivationDate(right.pmcId, right.contractType)
      );
    }
    if (enableDelegationsScheduling && !innerRef.current?.values.productDates?.[productDateKey]?.expirationDate) {
      const expirationDate = getPrefillExpirationDate(right.pmcId, right.contractType);
      if (expirationDate) {
        innerRef.current?.setFieldValue(`productDates.${productDateKey}.expirationDate`, expirationDate);
      }
    }
  };

  const fetchParkingProducts = () => {
    props.getProducts(appState.user.seasonTicketOwnerCrmId as string, appState.selectedLanguage);
  };

  const fetchDelegeeDetails = () => {
    props.getDelegeeDetails(fleetManagerState.selectedDelegeeId, appState.selectedLanguage, appState.user.seasonTicketOwnerCrmId!);
  };

  const fetchBulkImportStatus = () => {
    // Only the latest check may mark the status as checked when switching tabs quickly.
    const requestId = ++bulkImportStatusRequestId.current;
    setIsBulkImportStatusChecked(false);
    Promise.resolve(props.getBulkImportStatus(appState.user.seasonTicketOwnerCrmId as string))
      .catch(() => undefined)
      .finally(() => {
        if (isMounted.current && requestId === bulkImportStatusRequestId.current) {
          setIsBulkImportStatusChecked(true);
        }
      });
  };

  const initForm = () => {
    fetchParkingProducts();

    if (isEditMode) {
      fetchDelegeeDetails();
    }
  };

  const resetDelegeeForm = () => {
    props.disposeError();
    props.clearDelegeeDetails();
    props.resetBulkImportProgress();
  };

  useEffect(() => {
    initForm();

    return () => resetDelegeeForm();
    // eslint-disable-next-line
  }, [fleetManagerState.selectedDelegeeId, props.isEditMode]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (fleetManagerState.delegationType === DelegationType.Multiple) {
      fetchBulkImportStatus();
    }
    // eslint-disable-next-line
  }, [fleetManagerState.delegationType]);

  useEffect(() => {
    setFormFields();
    // eslint-disable-next-line
  }, [props.delegationFormDetails.delegeeDetails.data, props.delegationFormDetails.parkingProducts]);

  useEffect(() => {
    if (hasNoCapacity(errorConstants.keys.parkingRights) || hasNoCapacity(errorConstants.keys.pedestrianRights)) {
      fetchParkingProducts();
      handleDeselectAll();
    }
    // eslint-disable-next-line
  }, [props.delegationFormDetails.error?.errors]);

  const setFormFields = () => {
    innerRef.current?.setFieldValue("name", props.delegationFormDetails.delegeeDetails.data?.name ?? "");
    innerRef.current?.setFieldValue(
      "email",
      props.delegationFormDetails.delegeeDetails.data?.emailAddress ?? ""
    );

    const rightsObjs: IRightSelection[] = props.delegationFormDetails.delegeeDetails.data != null
      ? props.delegationFormDetails.delegeeDetails.data.products!.map(p => {
        return {
          pmcId: p.pmcId, contractType: p.contractType
        };
      })
      : []

    innerRef.current?.setFieldValue("rights", rightsObjs);

    if (enableDelegationsScheduling) {
      props.delegationFormDetails.delegeeDetails.data?.products?.forEach((product) => {
        const productDateKey = getProductDateKey(product.pmcId, product.contractType);

        innerRef.current?.setFieldValue(
          `productDates.${productDateKey}.activationDate`,
          product.activationDate
            ? parseISO(product.activationDate.split("T")[0])
            : getPrefillActivationDate(product.pmcId, product.contractType)
        );
        innerRef.current?.setFieldValue(
          `productDates.${productDateKey}.expirationDate`,
          product.expirationDate
            ? parseExpirationDateValue(product.expirationDate)
            : getPrefillExpirationDate(product.pmcId, product.contractType)
        );
      });
    }
  };

  const handleRightChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    parkingRights: IRightSelection[],
    validate: () => Promise<any>
  ) => {
    const { value } = event.target;
    const [pmcIdStr, contractTypeStr] = value.split("-");
    const rightObj: IRightSelection = { pmcId: parseInt(pmcIdStr), contractType: parseInt(contractTypeStr) };

    let rights = [...parkingRights];
    const exists = rights.some(r => r.pmcId === rightObj.pmcId && r.contractType === rightObj.contractType);
    if (exists) {
      rights = rights.filter(r => !(r.pmcId === rightObj.pmcId && r.contractType === rightObj.contractType));
    } else {
      rights.push(rightObj);
      ensureProductDatesInitialized(rightObj);
    }

    innerRef.current?.setFieldValue("rights", rights);
    props.disposeError();

    Promise.resolve().then(() => {
      validate();
    });
  };

  const handleSelectAll = (parkingRights: IRightSelection[]) => {
    innerRef.current?.setFieldValue("rights", parkingRights);
    if (enableDelegationsScheduling) {
      parkingRights.forEach((right) => ensureProductDatesInitialized(right));
    }
  };

  const handleDeselectAll = () => {
    innerRef.current?.setFieldValue("rights", []);
  };

  const submitCallback = () => {
    refreshHandler();
    onClose();
  };

  const handleSchedulingError = useCallback((productDateKey: string, error: SchedulingDateRangePickerError | undefined) => {
    setSchedulingErrors((errors) => (
      errors[productDateKey] === error
        ? errors
        : { ...errors, [productDateKey]: error }
    ));
  }, []);

  const handleFormSubmit = (values: IDelegeeFormValues) => {
    const hasSchedulingError = enableDelegationsScheduling && values.rights?.some((right) => (
      schedulingErrors[getProductDateKey(right.pmcId, right.contractType)]
    ));
    if (hasSchedulingError) {
      return;
    }

    if (fleetManagerState.delegationType === DelegationType.Multiple) {
      goToUploadFile();
      return;
    }
    const products = props.delegationFormDetails.parkingProducts.data!
      .filter(product => values.rights!.some(r => r.pmcId === product.pmcId && r.contractType === product.contractType))
      .map((selectedProduct): Product => {
        const productDateKey = getProductDateKey(selectedProduct.pmcId!, selectedProduct.contractType);
        return {
          pmcId: selectedProduct.pmcId!,
          contractType: selectedProduct.contractType,
          productType: selectedProduct.productType,
          activationDate: enableDelegationsScheduling
            ? getLocalizedDate(values.productDates?.[productDateKey]?.activationDate ?? null, false)
            : undefined,
          expirationDate: enableDelegationsScheduling
            ? getLocalizedDate(values.productDates?.[productDateKey]?.expirationDate ?? null, true)
            : undefined,
        };
      });
    const model: ParkingRightsRequestModel = {
      seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
      email: values.email,
      name: values.name,
      pmcIds: values.rights?.map(r => r.pmcId) ?? [],
      products: products
    };

    if (isEditMode) {
      model.registrationId = props.delegationFormDetails.delegeeDetails.data?.id!;
      model.sendRevocationConfirmationEmail = sendRevocationConfirmationEmail;

      props.updateDelegation(model, submitCallback);
      return;
    }

    props.inviteDelegee(model, submitCallback);
  };

  const handleEmailChange = (event: React.ChangeEvent<any>): void => {
    const { value } = event.target;

    innerRef.current?.setFieldValue("email", value);

    props.disposeError();
  };

  const submitButtonText = () => {
    if (fleetManagerState.delegationType === DelegationType.Multiple) {
      return t("goToUpload");
    }

    return !isEditMode ? t("sendInvite") : t("updateDelegee");
  };

  const getFormTitle = () => {
    if (isEditMode) {
      return t("editExistingDelegee");
    }

      return t(props.translationContextName + ":formTitle");
  }

  const delegeeEmailExists = (): boolean => checkEmailExists(props.delegationFormDetails.error);

  const hasNoCapacity = (...keys: string[]): boolean => keys.some(key => checkHasNoCapacity(props.delegationFormDetails.error, key));
  const delegateSingle = (): void => setDelegationType(DelegationType.Single);
  const delegateMultiple = (): void => setDelegationType(DelegationType.Multiple);
  const goToUploadFile = (): void => setDragAndDropView(true);

  // Keep selected products and scheduling dates in the import context for bulk validation after navigating to the upload step.
  const handleParkingRightsStateChange = (parkingRights: ICheckParkingRight[], values: IDelegeeFormValues) => {
    setCheckParkingRights(parkingRights.filter(x => x.checked).map((parkingRight) => {
      const productDateKey = getProductDateKey(parkingRight.right!.pmcId!, parkingRight.right!.contractType);
      return {
        ...parkingRight,
        activationDate: values.productDates?.[productDateKey]?.activationDate
          ?? (enableDelegationsScheduling ? getPrefillActivationDate(parkingRight.right!.pmcId!, parkingRight.right!.contractType) : null),
        expirationDate: values.productDates?.[productDateKey]?.expirationDate
          ?? (enableDelegationsScheduling
            ? getPrefillExpirationDate(parkingRight.right!.pmcId!, parkingRight.right!.contractType)
            : null),
      };
    }));
  };

  // Update Formik for the current form and import context for a possible later bulk validation request.
  const handleActivationDateChange = (pmcId: number, contractType: number, date: Date | null, productDateKey: string) => {
    innerRef.current?.setFieldValue(`productDates.${productDateKey}.activationDate`, date);
    setCheckParkingRights(importDelegationsState.checkParkingRights.map((parkingRight) =>
      parkingRight.right?.pmcId === pmcId && parkingRight.right.contractType === contractType
        ? { ...parkingRight, activationDate: date }
        : parkingRight
    ));
  };

  // Update Formik for the current form and import context for a possible later bulk validation request.
  const handleExpirationDateChange = (pmcId: number, contractType: number, date: Date | null, productDateKey: string) => {
    innerRef.current?.setFieldValue(`productDates.${productDateKey}.expirationDate`, date);
    setCheckParkingRights(importDelegationsState.checkParkingRights.map((parkingRight) =>
      parkingRight.right?.pmcId === pmcId && parkingRight.right.contractType === contractType
        ? { ...parkingRight, expirationDate: date }
        : parkingRight
    ));
  };

  const initiallySelectedPmcIds = useMemo(() => props.delegationFormDetails.delegeeDetails.data?.products?.map(p => p.pmcId), [props.delegationFormDetails.delegeeDetails.data]);

  const { importDelegationsState, setCheckParkingRights } = useImportDelegationsContext();

  const initiallySelectedRights = useMemo(
    () =>
      initiallySelectedPmcIds?.map((pmcId) => {
        const product = props.delegationFormDetails.parkingProducts.data?.find(
          (p) => p.pmcId === pmcId
        );
        return product
          ? { pmcId, contractType: product.contractType }
          : { pmcId, contractType: 1 };
      }),
    [initiallySelectedPmcIds, props.delegationFormDetails.parkingProducts.data]
  );

  const parkingProducts = props.delegationFormDetails.parkingProducts.data!;
  const parkingProductsFiltered = useMemo(() => {
    return parkingProducts;
  }, [parkingProducts]);

  const isMultipleDelegation = fleetManagerState.delegationType === DelegationType.Multiple;
  const shouldShowUploadInProgressWarning = () =>
    isBulkImportStatusChecked &&
    (isMultipleDelegation || props.shouldBlockForSingle) &&
    !props.delegationFormDetails.isBulkImportCompleted;
  const isSubmitDisabled = () =>
    shouldShowUploadInProgressWarning() || (isMultipleDelegation && !isBulkImportStatusChecked);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      innerRef={innerRef}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleSubmit, handleChange, values, errors, validateForm }) => (
        <div className="form-container">
          <div className="form-header">
            <Container className="form-header-content">
              {isEditMode && (
                <DeleteWrapper onClick={onDelete}>
                  <DeleteDelegeeIcon />
                  <DeleteText>{t("deleteDelegee")}</DeleteText>
                </DeleteWrapper>
              )}
              <Heading width="auto" justifyContent="center" fontSize={typography.fontSizeLarge}>
                {getFormTitle()}
              </Heading>
              <Button onClick={onClose} classes={{ root: "close-icon-btn" }} disableRipple>
                <CloseOutlined classes={{ root: "close-icon" }} />
              </Button>
            </Container>
          </div>
          <form noValidate className="delegee-form">
            {!isEditMode && !props.disableMultipleOption && (
              <Container>
                <NoPaddingBottomGrid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <NoPaddingBottomGrid container item direction="column" xs={12}>
                    <div className="delegation-type-wrapper">
                      <div className="delegation-type-selector">
                        <ButtonGroup variant="contained" fullWidth disableRipple>
                          <Button
                            onClick={delegateSingle}
                            color={
                              fleetManagerState.delegationType === DelegationType.Single
                                ? "primary"
                                : "default"
                            }
                            disableElevation
                          >
                            {t("single")}
                          </Button>
                          <Button
                            onClick={delegateMultiple}
                            color={
                              fleetManagerState.delegationType === DelegationType.Multiple
                                ? "primary"
                                : "default"
                            }
                            disableElevation
                          >
                            {t("multiple")}
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  </NoPaddingBottomGrid>
                </NoPaddingBottomGrid>
              </Container>
            )}
            <div className="form-body">
              <Container>
                {fleetManagerState.delegationType == DelegationType.Single && (
                  <React.Fragment>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                      <Heading fontSize={typography.fontSizeBase}>
                        <strong>{t("delegeeInformation")}</strong>
                      </Heading>
                    </Grid>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                      <Grid container direction="column" xs={3} item>
                        <InputLabel htmlFor="name">{t("globals:name")}</InputLabel>
                      </Grid>
                      <Grid container direction="column" xs={9} item>
                        <CustomInput
                          error={Boolean(errors.name)}
                          id="name"
                          name="name"
                          autoComplete="off"
                          value={values.name}
                          onChange={handleChange}
                          classes={{ root: "form-input" }}
                          variant="outlined"
                          placeholder={t(props.translationContextName + ":namePlaceholder")}
                          helperText={errors.name}
                        />
                      </Grid>
                    </Grid>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                      <Grid container direction="column" xs={3} item>
                        <RequiredInputLabel fontSize={typography.fontSizeSmaller} htmlFor="email">
                          {t("globals:email")}
                        </RequiredInputLabel>
                      </Grid>
                      <Grid container direction="column" xs={9} item>
                        <CustomInput.Required
                          error={Boolean(errors.email) || delegeeEmailExists()}
                          id="email"
                          name="email"
                          autoComplete="off"
                          value={values.email}
                          onChange={handleEmailChange}
                          classes={{ root: "form-input" }}
                          variant="outlined"
                          placeholder={t(props.translationContextName + ":emailPlaceholder")}
                          helperText={
                            delegeeEmailExists() ? t("errors:emailExists") : errors.email
                          }
                          disabled={isEditMode}
                        />
                      </Grid>
                    </Grid>

                    {isEditMode && (
                      <Grid container direction="row" justifyContent="space-between" alignItems="center" style={{ marginTop: "-1rem" }}>
                        <Grid container direction="column" xs={3} item />
                        <Grid container direction="column" xs={9} item>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                            <qp-switch
                              data-size="s"
                              data-checked={sendRevocationConfirmationEmail}
                              onClick={() => setSendRevocationConfirmationEmail(!sendRevocationConfirmationEmail)}
                            />
                            <span>{t("sendRevocationEmail")}</span>
                          </div>
                        </Grid>
                      </Grid>
                    )}

                    <Spacer />
                  </React.Fragment>
                )}
                <Translation ns="delegeeForm">{(t) => (
                  <ParkingRightsGrid
                    data={parkingProductsFiltered}
                    initiallySelectedRights={initiallySelectedRights}
                    disableProductsWithoutFirstAvailableContractStartDate={enableDelegationsScheduling}
                    loading={props.delegationFormDetails.parkingProducts.loading}
                    onStateChange={(parkingRights) => handleParkingRightsStateChange(parkingRights, values)}
                    onChange={(event) => handleRightChange(event, values.rights ?? [], validateForm)}
                    onSelectAll={(rights) => handleSelectAll(rights)}
                    onDeselectAll={handleDeselectAll}
                    error={{
                      hasError: Boolean(errors.rights),
                      message: errors.rights,
                    }}
                    title={t(props.translationContextName + ":formProductTitle")}
                    required={!isEditMode}
                    selectAllTooltipText={t(props.translationContextName + ":selectTooltip")}
                    deselectAllTooltipText={t(props.translationContextName + ":deselectTooltip")}
                    selectAllText={t("selectAllButton")}
                    deselectAllText={t("deselectAllButton")}
                    renderRowExpansion={enableDelegationsScheduling
                      ? (pr) => {
                          const pmcId = pr.right!.pmcId!;
                          const productDateKey = getProductDateKey(pmcId, pr.right!.contractType);
                          const delegeeProduct = isEditMode
                            ? props.delegationFormDetails.delegeeDetails.data?.products?.find(
                              (product) => product.pmcId === pmcId && product.contractType === pr.right!.contractType
                            )
                            : undefined;
                          const prefillActivationDate = getPrefillActivationDate(pmcId, pr.right!.contractType);
                          const prefillExpirationDate = getPrefillExpirationDate(pmcId, pr.right!.contractType);
                          const defaultActivationDate = delegeeProduct?.activationDate
                            ? parseDateValue(delegeeProduct.activationDate) ?? prefillActivationDate
                            : prefillActivationDate;
                          const defaultExpirationDate = delegeeProduct?.expirationDate
                            ? parseExpirationDateValue(delegeeProduct.expirationDate) ?? prefillExpirationDate
                            : prefillExpirationDate;

                          return (
                            <SchedulingDateRangePicker
                              defaultActivationDate={defaultActivationDate}
                              defaultExpirationDate={defaultExpirationDate}
                              prefillActivationDate={getPrefillActivationDate(pmcId, pr.right!.contractType)}
                              prefillExpirationDate={getPrefillExpirationDate(pmcId, pr.right!.contractType)}
                              activationDateLabel={t("vehicleForm:activationDate")}
                              expirationDateLabel={t("vehicleForm:expirationDate")}
                              onActivationDateChange={(date) => handleActivationDateChange(pmcId, pr.right!.contractType, date, productDateKey)}
                              onExpirationDateChange={(date) => handleExpirationDateChange(pmcId, pr.right!.contractType, date, productDateKey)}
                              onError={(error) =>
                                handleSchedulingError(productDateKey, error)
                              }
                            />
                          );
                        }
                      : undefined}
                  />
                )}</Translation>



                {shouldShowUploadInProgressWarning() && (
                  <div className="bulkimport-inprogress-warning">
                    <WarningColumn
                      title={t("bulkImportInProgressWarningTitle")}
                      details={t("bulkImportInProgressWarningDescription")}
                      orangeWarning={true}
                    />
                  </div>
                )}

              </Container>
            </div>
          </form>
          <CustomError
            show={checkValidationError(
              props.delegationFormDetails.error,
              errorConstants.keys.entryRights,
              errorConstants.codes.quantityExceeded
            )}
            message={t("errors:quantityExceeded")}
          />
          <CustomError
            show={checkValidationError(
              props.delegationFormDetails.error,
              errorConstants.keys.entryRights,
              errorConstants.codes.datesNotInContractBounds
            )}
            message={t("errors:datesNotInContractBounds")}
          />
          <CustomError
            show={checkValidationError(
              props.delegationFormDetails.error,
              errorConstants.keys.entryRights,
              errorConstants.codes.activationDateBeforePassCommercialStartDate
            )}
            message={t("errors:activationDateBeforePassCommercialStartDate")}
          />
          <CustomError show={hasNoCapacity(errorConstants.keys.parkingRights)} message={t("errors:noCapacity")} />
          <CustomError show={hasNoCapacity(errorConstants.keys.pedestrianRights, errorConstants.keys.entryRights)} message={t("errors:noCapacityEntryRights")} />
          <div className="form-footer">
            <Container className="form-footer-content">
              <React.Fragment>
                <CustomButton.Light onClick={onClose} text={t("globals:close")} id="close-invite" />
                <CustomButton.Primary
                  loading={props.delegationFormDetails.loading}
                  onClick={() => handleSubmit()}
                  text={submitButtonText()}
                  id="submit-invite"
                  disabled={isSubmitDisabled()}
                />
              </React.Fragment>
            </Container>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default DelegationForm;
